import { ExternalLinkIcon, WarningIcon } from "@chakra-ui/icons";
import { Collapse, Flex, Link, Text } from "@chakra-ui/react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaForm } from "@nens/ranalib";
import { humanizeBytes } from "@nens/shared-utils";

import { DetailRow, DetailTable } from "@/components/detail-table";
import { RevisionTaskDetail, useLatestRevisionTask } from "@/queries/query-hcc-revision-task";
import { getPrettyDateTimeDetailed } from "@/utils/get-pretty-time";

import { useSchematisationDetailsContext } from "../schematisation-details-context";

import { ModelCheckerErrors } from "./model-checker-errors";
import { ModelSchematisationTagsEditor } from "./model-schematisation-tags-editor";

interface ModelSchematisationMetadataTableProps {
  editing: boolean;
  tags: string[];
  setTags: (tags: string[] | ((prevTags: string[]) => string[])) => void;
}

export function ModelSchematisationMetadataTable({
  editing,
  tags,
  setTags,
}: ModelSchematisationMetadataTableProps) {
  const { schematisation, revision, threediModel } = useSchematisationDetailsContext();

  const intl = useIntl();
  const latestRevisionTask = useLatestRevisionTask(schematisation.id, revision.id);

  const schematisationDescription = schematisation.meta?.description ?? "";
  const commitMessage = revision.commitMessage ?? "";
  const taskDetail = latestRevisionTask?.detail as RevisionTaskDetail | null | undefined;
  const taskErrors = taskDetail?.result?.errors?.map((error) => error.description) ?? [];
  const downloadTaskUrl = latestRevisionTask?.url;
  const totalStorageBytes = (schematisation.storageUsage ?? 0) + (threediModel?.storageSpace ?? 0);

  const rows: DetailRow[] = [
    {
      name: intl.formatMessage({ id: "file.created_on", defaultMessage: "Created on" }),
      value: moment(schematisation.created).format("D MMMM YYYY, HH:mm"),
    },
    {
      name: intl.formatMessage({
        id: "file.schematisation_id",
        defaultMessage: "Model schematisation id",
      }),
      value: schematisation.id || "-",
    },
    {
      id: "description",
      name: intl.formatMessage({ id: "file.description", defaultMessage: "Description" }),
      value: (
        <>
          <Collapse unmountOnExit in={editing}>
            <RanaForm.Textarea
              field="description"
              placeholder={intl.formatMessage({
                id: "file.add_description",
                defaultMessage: "Add a description",
              })}
              size="sm"
              m={0}
            />
          </Collapse>

          <Collapse in={!editing}>{schematisationDescription || "-"}</Collapse>
        </>
      ),
    },
    {
      id: "commit_message",
      name: intl.formatMessage({
        id: "file.revision.commit_message",
        defaultMessage: "Commit message",
      }),
      value: (
        <>
          <Collapse unmountOnExit in={editing}>
            <RanaForm.Textarea
              field="commitMessage"
              placeholder={intl.formatMessage({
                id: "file.add_commit_message",
                defaultMessage: "Add a commit message",
              })}
              size="sm"
              m={0}
            />
          </Collapse>

          <Collapse in={!editing}>{commitMessage || "-"}</Collapse>
        </>
      ),
    },
    {
      id: "tags",
      name: intl.formatMessage({ id: "file.tags", defaultMessage: "Tags" }),
      value: <ModelSchematisationTagsEditor editing={editing} tags={tags} setTags={setTags} />,
    },
    {
      name: intl.formatMessage({ id: "file.storage", defaultMessage: "Storage" }),
      value: humanizeBytes(totalStorageBytes),
    },
    {
      name: intl.formatMessage({
        id: "file.revision.number",
        defaultMessage: "Revision number",
      }),
      value: revision.number ? `#${revision.number}` : undefined,
    },
    {
      name: intl.formatMessage({
        id: "file.revision.id",
        defaultMessage: "Revision id",
      }),
      value: revision.id,
    },
    {
      name: intl.formatMessage({
        id: "file.revision.committed_by",
        defaultMessage: "Revision committed by",
      }),
      value:
        [revision.commitFirstName, revision.commitLastName].filter(Boolean).join(" ") ||
        revision.commitUser,
    },
    {
      name: intl.formatMessage({
        id: "file.revision.commit_date",
        defaultMessage: "Revision committed on",
      }),
      value: revision.commitDate ? getPrettyDateTimeDetailed(revision.commitDate) : undefined,
    },
    {
      name: intl.formatMessage({
        id: "file.revision.schematisation_check",
        defaultMessage: "Model schematisation check",
      }),
      value:
        revision.isValid === false ? (
          <Flex alignItems="center" gap={2}>
            <WarningIcon color="semantic.error.main" boxSize="6" />{" "}
            <Text>
              <FormattedMessage
                id="file.revision.invalid"
                defaultMessage="This revision is <strong>invalid</strong>"
                values={{
                  strong: (chunks) => (
                    <Text as="strong" color="semantic.error.main" display="inline">
                      {chunks}
                    </Text>
                  ),
                }}
              />
            </Text>
          </Flex>
        ) : (
          <FormattedMessage id="file.revision.valid" defaultMessage="This revision is valid" />
        ),
      action: taskErrors.length ? (
        <ModelCheckerErrors
          errors={taskErrors}
          downloadTask={downloadTaskUrl ? () => window.open(downloadTaskUrl, "_blank") : undefined}
        />
      ) : undefined,
    },
  ];

  rows.push({
    name: intl.formatMessage({
      id: "file.external_link",
      defaultMessage: "External link",
    }),
    value: schematisation.id ? (
      <Link href={`/hcc-management/schematisations/${schematisation.id}/${revision.id}`} isExternal>
        <Text color="brand.analytic">
          <FormattedMessage id="file.hcc_management_page" defaultMessage="Rana HCC page" />
          <ExternalLinkIcon ml={1} mb={1} />
        </Text>
      </Link>
    ) : (
      <FormattedMessage id="file.no_link_available" defaultMessage="There is no link available" />
    ),
  });

  return <DetailTable rows={rows} />;
}
